import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { GoalPeriodStatus } from 'src/app/enums/goal-period-status.enum';
import { GoalPeriod } from 'src/app/models/goalperiod.model';
import { ReviewPeriodGoalSettings } from 'src/app/models/review-period-goal-settings.model';

interface ChecklistItem {
    labelKey: string;
    tooltipKey?: string;
    completed: number;
    total: number;
    isComplete: boolean;
    isActive: boolean;
}

@Component({
    selector: 'app-goal-checklist',
    templateUrl: './goal-checklist.component.html',
    styleUrls: ['./goal-checklist.component.scss']
})
export class GoalChecklistComponent implements OnChanges {
    @Input() goalPeriod: GoalPeriod;

    public checklistItems: ChecklistItem[] = [];
    public hasWarning: boolean = false;
    public hasActiveItems: boolean = false;

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['goalPeriod'] && this.goalPeriod) {
            this.evaluateChecklist();
        }
    }

     // 1. Összes súlyozás megadva (Mindig aktív)
    private addWeightCheck(items: ChecklistItem[], settings: ReviewPeriodGoalSettings) {
        let totalWeights = 0;
        let completedWeights = 0;

        let totalTextBasedWeights = 0;

        let totalNumericWeights = 0;

        let hasTextBasedWeight = false;
        let hasNumericWeight = false;

        if (settings?.isTextBasedGoalsEnabled) {
            this.goalPeriod.textBasedGoals?.forEach(goal => {
                goal.actions?.forEach(action => {
                    totalTextBasedWeights++;
                    if (action.weight !== null && action.weight !== undefined) {
                        completedWeights++;
                        hasTextBasedWeight = true;
                    }
                });
            });

            if (hasTextBasedWeight) {
                totalWeights += totalTextBasedWeights;
            }
        }

        if (settings?.isNumericGoalsEnabled) {
            this.goalPeriod.numericGoals?.forEach(goal => {
                goal.kpiList?.forEach(kpi => {
                    totalNumericWeights++;
                    if (kpi.weight !== null && kpi.weight !== undefined) {
                        completedWeights++;
                        hasNumericWeight = true;
                    }
                });
            });

            if (hasNumericWeight) {
                totalWeights += totalNumericWeights;
            }
        }

        items.push({
            labelKey: 'GOAL_CHECKLIST.ALL_WEIGHTS_SPECIFIED',
            tooltipKey: 'GOAL_CHECKLIST.ALL_WEIGHTS_SPECIFIED_TOOLTIP',
            completed: completedWeights,
            total: totalWeights,
            isComplete: totalWeights > 0 ? completedWeights === totalWeights : true,
            isActive: totalWeights > 0 && (hasTextBasedWeight || hasNumericWeight)  // Only show if there are goals that need weights
        });
    }

    // 2. Összes önértékelés kitöltve (Szöveges)
    private addSelfAssessmentCheck(items: ChecklistItem[], settings: ReviewPeriodGoalSettings) {
        if (settings?.isTextBasedGoalsEnabled) {
            let totalSelfAssessments = 0;
            let completedSelfAssessments = 0;

            this.goalPeriod.textBasedGoals?.forEach(goal => {
                goal.actions?.forEach(action => {
                    const selfProgress = action.progress?.find(p => p.employeeId === this.goalPeriod.employeeId);
                    if (selfProgress) {
                        totalSelfAssessments++;
                        if (selfProgress.progress !== null && selfProgress.progress !== undefined) {
                            completedSelfAssessments++;
                        }
                    }
                });
            });

            items.push({
                labelKey: 'GOAL_CHECKLIST.ALL_SELF_ASSESSMENTS_FILLED',
                tooltipKey: 'GOAL_CHECKLIST.ALL_SELF_ASSESSMENTS_TOOLTIP',
                completed: completedSelfAssessments,
                total: totalSelfAssessments,
                isComplete: totalSelfAssessments > 0 ? completedSelfAssessments === totalSelfAssessments : true,
                isActive: this.goalPeriod.status >= GoalPeriodStatus.SelfAssessment && totalSelfAssessments > 0
            });
        }
    }

    // 3. Összes vezetői értékelés kitöltve (Szöveges)
    private addLeaderAssessmentCheck(items: ChecklistItem[], settings: ReviewPeriodGoalSettings) {
        if (settings?.isTextBasedGoalsEnabled) {
            let totalLeaderAssessments = 0;
            let completedLeaderAssessments = 0;

            this.goalPeriod.textBasedGoals?.forEach(goal => {
                goal.actions?.forEach(action => {
                    // Keresünk olyan progresst ami NEM az értékelt személyé
                    const leaderProgressList = action.progress?.filter(p => p.employeeId !== this.goalPeriod.employeeId);
                    
                    // Lehet több főnöke is a személynek és mindegyiknek értékelnie kell ezért össze kell számolni
                    leaderProgressList?.forEach(progress => {
                        totalLeaderAssessments++;
                        if (progress.progress !== null && progress.progress !== undefined) {
                            completedLeaderAssessments++;
                        }
                    });

                });
            });

            items.push({
                labelKey: 'GOAL_CHECKLIST.ALL_LEADER_ASSESSMENTS_FILLED',
                tooltipKey: 'GOAL_CHECKLIST.ALL_LEADER_ASSESSMENTS_TOOLTIP',
                completed: completedLeaderAssessments,
                total: totalLeaderAssessments,
                isComplete: totalLeaderAssessments > 0 ? completedLeaderAssessments === totalLeaderAssessments : true,
                isActive: this.goalPeriod.status >= GoalPeriodStatus.LeaderAssessment && totalLeaderAssessments > 0
            });
        }
    }

    // 4. Összes elért eredmény megadva (OKR)
    private addAchievedResultsCheck(items: ChecklistItem[], settings: ReviewPeriodGoalSettings) {
        if (settings?.isNumericGoalsEnabled) {
            let totalOkrs = 0;
            let completedOkrs = 0;

            this.goalPeriod.numericGoals?.forEach(goal => {
                goal.kpiList?.forEach(kpi => {
                    totalOkrs++;
                    if (kpi.achieved !== null && kpi.achieved !== undefined) {
                        completedOkrs++;
                    }
                });
            });

            items.push({
                labelKey: 'GOAL_CHECKLIST.ALL_ACHIEVED_RESULTS_SPECIFIED',
                tooltipKey: 'GOAL_CHECKLIST.ALL_ACHIEVED_RESULTS_TOOLTIP',
                completed: completedOkrs,
                total: totalOkrs,
                isComplete: totalOkrs > 0 ? completedOkrs === totalOkrs : true,
                isActive: this.goalPeriod.status >= GoalPeriodStatus.SelfAssessment && totalOkrs > 0
            });
        }
    }

    private evaluateChecklist(): void {
        const status = this.goalPeriod.status;
        const settings = this.goalPeriod.reviewPeriod?.reviewPeriodGoalSettings;
        
        let items: ChecklistItem[] = [];

        // 1. Összes súlyozás megadva (Mindig aktív)
        this.addWeightCheck(items, settings);

        // 2. Összes önértékelés kitöltve (Szöveges)
        this.addSelfAssessmentCheck(items, settings);
        
        // 3. Összes vezetői értékelés kitöltve (Szöveges)
        this.addLeaderAssessmentCheck(items, settings);
        
        // 4. Összes elért eredmény megadva (OKR)
        this.addAchievedResultsCheck(items, settings);

        this.checklistItems = items.filter(i => i.isActive);
        this.hasActiveItems = this.checklistItems.length > 0;
        this.hasWarning = this.checklistItems.some(i => !i.isComplete);
    }
}
